import * as Sentry from '@sentry/react';

const REJECTION_PATTERNS = [
  'user rejected',
  'user denied',
  'user cancelled',
  'user canceled',
  'rejected by user',
  'request rejected',
];

/** Detects user-initiated wallet rejections (EIP-1193 code 4001). */
export function isUserRejection(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as any;

  if (err.name === 'UserRejectedRequestError' || err.code === 4001) return true;

  // viem wraps errors — use its walk() if available, otherwise traverse cause chain
  if (typeof err.walk === 'function') {
    if (err.walk((e: any) => e?.code === 4001)) return true;
  } else {
    let cause = err.cause;
    for (let i = 0; i < 5 && cause && typeof cause === 'object'; i++) {
      if (cause.name === 'UserRejectedRequestError' || cause.code === 4001) return true;
      cause = cause.cause;
    }
  }

  const msg = String(err.shortMessage || err.message || '').toLowerCase();
  return REJECTION_PATTERNS.some((p) => msg.includes(p));
}

function isNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as any;
  if (err.status === 404) return true;
  const msg = String(err.message || '');
  return msg.includes('404') || msg.toLowerCase().includes('not found');
}

// Rate limiting is transient by definition and callers back off/retry on their own
// (react-query refuses to retry 429s — see main.tsx); reporting each one is noise.
function isRateLimitError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const err = error as any;
  if (err.status === 429 || err.response?.status === 429) return true;
  return String(err.message || '')
    .toLowerCase()
    .includes('http 429');
}

/** Reports an error to Sentry, filtering out wallet rejections, 404s, and 429 rate limits. */
export function captureError(error: unknown, context: string, extra?: Record<string, unknown>): void {
  if (isUserRejection(error) || isNotFoundError(error) || isRateLimitError(error)) return;

  const errorToCapture = error instanceof Error ? error : new Error(String(error));
  Sentry.captureException(errorToCapture, { extra: { context, ...extra } });
}
